import { useEffect, useState, useCallback, useRef } from "react";
import { supabase } from "@/lib/supabase";

export interface User {
  id: string;
  email: string;
  role?: string;
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const pendingCheck = useRef(false);

  const verifyAdmin = useCallback(async (authUser: any): Promise<User | null> => {
    if (!authUser?.id) return null;
    try {
      const { data: adminRow, error } = await supabase
        .from("admin_users")
        .select("id, email, role")
        .eq("id", authUser.id)
        .maybeSingle();
      if (error) {
        console.error("Error verifying admin:", error);
        return null;
      }
      if (!adminRow) {
        return null;
      }
      const u = {
        id: adminRow.id,
        email: adminRow.email,
        role: adminRow.role || "admin",
      };
      setUser(u);
      return u;
    } catch (err) {
      console.error("Exception in verifyAdmin:", err);
      return null;
    }
  }, []);

  const checkSession = useCallback(async () => {
    if (pendingCheck.current) return;
    pendingCheck.current = true;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        await verifyAdmin(session.user);
      } else {
        setUser(null);
      }
    } catch (err) {
      console.error("Error checking session:", err);
      setUser(null);
    } finally {
      setLoading(false);
      pendingCheck.current = false;
    }
  }, [verifyAdmin]);

  useEffect(() => {
    checkSession();
    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_IN" && session?.user) {
        verifyAdmin(session.user).catch((err) => console.error("AuthStateChange verifyAdmin error:", err));
      } else if (event === "SIGNED_OUT") {
        setUser(null);
        setLoading(false);
      } else if (event === "USER_UPDATED" && session?.user) {
        verifyAdmin(session.user).catch((err) => console.error("AuthStateChange verifyAdmin error:", err));
      } else if (event === "INITIAL_SESSION") {
        if (session?.user) {
          verifyAdmin(session.user).catch((err) => console.error("AuthStateChange verifyAdmin error:", err));
        } else {
          setUser(null);
          setLoading(false);
        }
      }
    });
    return () => { listener.subscription.unsubscribe(); };
  }, [checkSession, verifyAdmin]);

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    if (data.user) {
      const u = await verifyAdmin(data.user);
      if (!u) {
        await supabase.auth.signOut();
        setUser(null);
        throw new Error("Usuario no autorizado como administrador.");
      }
    }
    return data;
  };

  const signUp = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
  };

  return { user, loading, signIn, signUp, signOut };
}